import { useId } from 'react';

import { cn } from '../lib/cn';

export interface SparklineProps {
  values: readonly number[];
  /** Clase de color del trazo (`text-chart-1`…): la línea y el relleno usan `currentColor`. */
  className?: string;
  /** Texto para lectores de pantalla (la tendencia en palabras). */
  label?: string;
}

/**
 * Tendencia mínima en SVG: línea de 2px + relleno degradado + punto en el último valor. Sin ejes ni
 * números: acompaña a una cifra, no la reemplaza.
 */
export function Sparkline({ values, className, label }: SparklineProps) {
  const gid = useId();
  const w = 120;
  const h = 32;
  const pad = 3;
  if (values.length < 2) return null;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = max - min || 1;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * w;
    const y = pad + (1 - (v - min) / span) * (h - pad * 2);
    return [x, y] as const;
  });
  const line = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join('');
  const last = pts[pts.length - 1] ?? [w, h];
  return (
    <svg
      viewBox={`0 0 ${String(w)} ${String(h)}`}
      preserveAspectRatio="none"
      className={cn('h-8 w-full overflow-visible', className)}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      <defs>
        <linearGradient id={gid} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="currentColor" stopOpacity="0.22" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${line}L${String(w)} ${String(h)}L0 ${String(h)}Z`} fill={`url(#${gid})`} />
      <path
        d={line}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      <circle
        cx={last[0]}
        cy={last[1]}
        r="2.5"
        fill="currentColor"
        className="stroke-card"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
